import React,{useState} from "react";
import { refreshNotifications } from "../utils/notifications";
import { formatUsPhone, findContactByNumber, contactDisplayName, conversationDisplayName } from "../utils/contacts";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import {
  Grid,
} from "@mui/material";
import CallMissedIcon from "@mui/icons-material/CallMissedOutlined";
import VoicemailIcon from "@mui/icons-material/VoicemailOutlined";
import FaxIcon from "@mui/icons-material/FaxOutlined";
import RecordingIcon from "@mui/icons-material/GraphicEqOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import EnhancedTable from "./enhancedTable";
import ListParty from "./listParty";
import { formatWhen } from "../utils/format";
import {
  updateCallJournal,
  updateVoicemailViewed,
  updateRecordingsViewed,
  updateFaxes,
  updateConversationsViewed,
} from "../utils/utils";
import {
  setNotifications,
  setNotificationsLoading,
} from "../store/features/notification/notificationSlice";
import { setCalls } from "../store/features/calls/callSlice";
import { setVoicemails } from "../store/features/voicemails/voicemailSlice";
import { setSmsConversations } from "../store/features/smsConversations/smsConversationsSlice";
import { setFaxes } from "../store/features/faxes/faxSlice";
import { setRecordings } from "../store/features/recordings/recordingSlice";
import { resetSession } from "../store/features/session/sessionSlice";

const mapDispatchToProps = {
  setCalls,
  setVoicemails,
  setSmsConversations,
  setFaxes,
  setRecordings,
  setNotifications,
  setNotificationsLoading,
  resetSession,
};

const headCells = {
  call: [
    {
      id: "from",
      numeric: false,
      disablePadding: false,
      label: "From",
    },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "dateTime", numeric: true, disablePadding: false, label: "Date" }
  ],
  voicemail: [
    {
      id: "from",
      numeric: false,
      disablePadding: false,
      label: "From",
    },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "dateTime", numeric: true, disablePadding: false, label: "Date" }
  ],
  fax: [
    {
      id: "from",
      numeric: false,
      disablePadding: false,
      label: "From",
    },
    { id: "title", numeric: true, disablePadding: false, label: "Title" },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "dateTime", numeric: true, disablePadding: false, label: "Date" }
  ],
  recording: [
    {
      id: "title",
      numeric: false,
      disablePadding: false,
      label: "Title",
    },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "dateTime", numeric: true, disablePadding: false, label: "Date" }
  ],
  conversation: [
    { id: "from", numeric: false, disablePadding: false, label: "From" },
    { id: "unreadMessages", numeric: true, disablePadding: false, label: "Unread" },
    { id: "dateTime", numeric: true, disablePadding: false, label: "Last message" },
  ]
};

const useStyles = makeStyles()((theme) => ({
  when: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  text: {
    fontSize: 14.5,
    color: theme.palette.text.primary,
  },
  muted: {
    color: theme.palette.text.disabled,
  },
  unread: {
    minWidth: 22,
    height: 22,
    padding: "0 7px",
    borderRadius: 11,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12.5,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
  },
}));

const NotificationsTable = (props) => {
  const { classes } = useStyles();
  const { 
    title, 
    menu, 
    menuItems, 
    tab, 
    history, 
    notifications,
    session,
    phonebook,
    externalPhonebook,
    personalPhonebook,
    resetSession,
    setCalls,
    setVoicemails,
    setSmsConversations,
    setFaxes,
    setRecordings,
    setNotifications,
    setNotificationsLoading,
  } = props;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Name of a personal contact whose number matches, or "".
  const personalName = (number) => contactDisplayName(findContactByNumber([personalPhonebook], number));

  // The first cell of a row: a name with the number underneath, or just the
  // formatted number.
  const party = (icon, tone, name, number) => {
    const shownNumber = formatUsPhone(number);
    const shownName = String(name || "").trim();
    return (
      <ListParty
        icon={icon}
        tone={tone}
        title={shownName !== "" ? shownName : (shownNumber || "Unknown")}
        detail={shownName !== "" && shownNumber !== shownName ? shownNumber : ""}
      />
    );
  };

  const when = (dateTime) => <span className={classes.when}>{formatWhen(dateTime)}</span>;

  // Calls and voicemails: the server's name, otherwise a personal contact's.
  const getFromName = (item) => {
    const serverName = item.hasOwnProperty("name") ? String(item.name || "").trim() : "";
    return serverName !== "" ? serverName : personalName(item.number);
  };

  // SMS: resolved the same way as on the SMS page (the conversation's own
  // name, then personal, internal and external contacts).
  const getConversationName = (item) =>
    conversationDisplayName(item, [personalPhonebook, phonebook, externalPhonebook]);

  // Search: any of the given names or titles, or a number typed any way.
  const term = searchTerm.trim().toLowerCase();
  const searching = term.length >= 2;
  const termDigits = /^[\d\s()+.-]+$/.test(term) ? term.replace(/\D/g, "") : "";
  const matchesSearch = (texts, numbers) => {
    if (!searching) return true;
    if (texts.some((text) => String(text || "").toLowerCase().includes(term))) return true;
    return numbers.some((value) => {
      const number = String(value || "");
      if (number === "") return false;
      if (number.toLowerCase().includes(term)) return true;
      if (termDigits.length < 2) return false;
      const digits = number.replace(/\D/g, "");
      return digits.includes(termDigits) ||
        (termDigits.length > 2 && termDigits[0] === "1" && digits.includes(termDigits.slice(1)));
    });
  };

  const generateRows = ()=>{
    switch(tab){
      case "call":
        return notifications.call.filter((item) => matchesSearch([getFromName(item)], [item.number])).map((item) => ({
          // Red for a call that was not answered.
          from: party(
            <CallMissedIcon />,
            String(item.state || "").toUpperCase() === "ANSWERED" ? "neutral" : "danger",
            getFromName(item),
            item.number
          ),
          status: item.state,
          dateTime: when(item.dateTime),
        }));
      case "voicemail":
        return notifications.voicemail.filter((item) => matchesSearch([getFromName(item)], [item.number])).map((item) => {
          return {
            from: party(<VoicemailIcon />, "brand", getFromName(item), item.number),
            status: item.type,
            dateTime: when(item.dateTime),
          };
        });
      case "fax":
        return notifications.fax.filter((item) => matchesSearch([personalName(item.from), item.title], [item.from])).map((item) => {
          const faxTitle = String(item.title || "").trim();
          return {
            from: party(<FaxIcon />, "brand", personalName(item.from), item.from),
            title: faxTitle !== ""
              ? <span className={classes.text}>{faxTitle}</span>
              : <span className={classes.muted}>—</span>,
            status: item.type,
            dateTime: when(item.dateTime || item.date),
          };
        });
      case "recording":
        return notifications.recording.filter((item) => matchesSearch([String(item.title || "").trim() || "Recording"], [])).map((item) => {
          return {
            title: (
              <ListParty
                icon={<RecordingIcon />}
                tone="brand"
                title={String(item.title || "").trim() || "Recording"}
              />
            ),
            status: item.type,
            dateTime: when(item.dateTime || item.date),
          };
        });
      case "conversation":
        return notifications.conversation.filter((item) => matchesSearch([getConversationName(item)], [item.number, item.number_pretty])).map((item) => {
          return {
            from: party(<SmsIcon />, "brand", getConversationName(item), item.number_pretty || item.number),
            unreadMessages: <span className={classes.unread}>{item.unreadMessages > 999 ? "999+" : item.unreadMessages}</span>,
            dateTime: when(item.lastMessage.dateTime),
            object: item,
          };
        });
      default:
        break;
    }
  }

  const refetchNotifications = () => refreshNotifications({
    values: { server: session.lastServer, token: session.token },
    setters: { setCalls, setFaxes, setRecordings, setVoicemails, setSmsConversations },
    setNotifications,
    setNotificationsLoading,
    // This page shows or changes the call journal: always the server's copy.
    freshJournal: true,
    freshSms: true,
    onUnauthorized: () => {
      resetSession();
      history.push('/login');
    },
  });

  const clearCurrentNotifications = () =>{
    let values = {
      server: session.lastServer,
      token: session.token,
    };
    if (notifications[tab].length > 0) {
      switch (tab) {
        case "call":
          values.call = notifications.call.map((item) => {
            return {
              jid: item.jid,
              viewed: "yes",
              type: item.type,
              direction: item.direction
            };
          });
          try {
            updateCallJournal(values);
            refetchNotifications();
          } catch (error) {
            console.error(error);
          }

          break;
        case "voicemail":
          values.voicemail = notifications.voicemail.map((item) => {
            return {
              vid: item.vid,
              viewed: "yes",
            };
          });

          try {
            updateVoicemailViewed(values);
            refetchNotifications();
          } catch (error) {
            console.error(error);
          }

          break;
        case "recording":
          values.recording = notifications.recording.map((item) => {
            return {
              rid: item.rid,
              viewed: "yes",
              type: "new",
              title: item.title,
            };
          });
          try {
            updateRecordingsViewed(values);
            refetchNotifications();
          } catch (error) {
            console.error(error);
          }
          break;
        case "fax":
          values.fax = notifications.fax.map((item) => {
            return {
              fid: item.fid,
              viewed: "yes",
              type: "new",
              group: item.group,
              title: item.title,
            };
          });
          try {
            updateFaxes(values);
            refetchNotifications();
          } catch (error) {
            console.error(error);
          }
          break;
        case "conversation":
          values.conversation = notifications.conversation.map((item) => {
            return {
              conversation_id: item.conversation_id,
            };
          });
          try {
            updateConversationsViewed(values);
            refetchNotifications();
          } catch (error) {
            console.error(error);
          }
          break;
        default:
          break;
      }
    }
  }

  const handleClickRow = (row) => {
    if (row.object && row.object.conversation_id) {
      history.push(`/dashboard/sms/sms/all/${row.object.conversation_id}`);
    }
  }

  // The tabs show how many notifications wait in each.
  const menuWithCounts = (menuItems || []).map((item) => ({
    ...item,
    count: Array.isArray(notifications[item.linkId]) ? notifications[item.linkId].length : 0,
  }));

  return (
    <Grid container>
      <Grid size={12}>
        <EnhancedTable
          headerData={headCells}
          rowData={generateRows()}
          handleRowClick={(tab === "conversation") ? handleClickRow : null }
          selectable={false}
          menu={menu ? menuWithCounts : false}
          searchFunc={(value) => { setSearchTerm(value); setPage(0); }}
          emptyText={searching ? "No notifications match your search." : undefined}
          title={title}
          tab={tab}
          history={history}
          hasClear={true}
          clearFunction={clearCurrentNotifications}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          page={page}
          useGroupFilter={false}
        />
      </Grid>
    </Grid>
  );
};

NotificationsTable.propTypes = {
  title: PropTypes.string.isRequired,
  menu: PropTypes.bool.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      linkId: PropTypes.string,
    })
  ),
  tab: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => {
  return {
    session: state.session,
    notifications: state.notifications,
    phonebook: state.phonebook,
    externalPhonebook: state.externalPhonebook,
    personalPhonebook: state.personalPhonebook,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsTable);